import { useState, useMemo, useRef, useEffect } from 'react';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';
import { LedgerEntry, Check } from '../../types';
import { ledgerApi, checksApi, allocationsApi } from '../../api';
import UnallocatedLedgerPanel from './UnallocatedLedgerPanel';
import UnallocatedChecksPanel from './UnallocatedChecksPanel';
import { useAccountPreference, useAccountMenuItem } from '../../hooks';

interface ManualPurchaseMatchTabProps {
  clientId: string;
}

export const ManualPurchaseMatchTab: React.FC<ManualPurchaseMatchTabProps> = ({ clientId }) => {
  const [selectedLedgerEntryId, setSelectedLedgerEntryId] = useState<string | null>(null);
  const [selectedCheckIds, setSelectedCheckIds] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  const [checkAmountFilter, setCheckAmountFilter] = useState<string | null>(null);
  const toast = useRef<Toast | null>(null);
  const { selectedAccountId } = useAccountPreference(clientId);
  const { accountMenuItem } = useAccountMenuItem(clientId);

  // Data loading state
  const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
  const [checks, setChecks] = useState<Check[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [clientId, selectedAccountId]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [ledgerData, checksData] = await Promise.all([
        ledgerApi.listLedgerEntries(clientId, { unallocatedOnly: true, accountId: selectedAccountId, entryType: 'purchase' }),
        checksApi.listApprovedChecks(clientId),
      ]);

      // Backend already filters to unallocated customer entries
      setLedgerEntries(ledgerData);
      // Unallocated checks: not yet linked to a ledger entry
      // (listApprovedChecks already filters for status='approved')
      // Also filter by account if one is selected
      // Only show checks with customer_id (required for creating allocations)
      setChecks(checksData.filter((c) =>
        !c.ledger_entry_id &&
        c.customer_id &&
        (!selectedAccountId || c.account_id === selectedAccountId)
      ));
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load data',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate min and max dates from data
  const { minDate, maxDate } = useMemo(() => {
    const parseDates = (items: Array<{ entry_date?: string | null; check_date?: string | null }>) => {
      return items
        .map((item) => {
          const dateStr = (item as LedgerEntry).entry_date || (item as Check).check_date;
          return dateStr ? new Date(dateStr) : null;
        })
        .filter((d): d is Date => d !== null && !isNaN(d.getTime()));
    };

    const ledgerDates = parseDates(ledgerEntries);
    const checkDates = parseDates(checks);

    const ledgerMin = ledgerDates.length > 0 ? new Date(Math.min(...ledgerDates.map((d) => d.getTime()))) : null;
    const ledgerMax = ledgerDates.length > 0 ? new Date(Math.max(...ledgerDates.map((d) => d.getTime()))) : null;
    const checkMin = checkDates.length > 0 ? new Date(Math.min(...checkDates.map((d) => d.getTime()))) : null;
    const checkMax = checkDates.length > 0 ? new Date(Math.max(...checkDates.map((d) => d.getTime()))) : null;

    // minDate: later of the two earliest dates
    const min = !ledgerMin ? checkMin : !checkMin ? ledgerMin : new Date(Math.max(ledgerMin.getTime(), checkMin.getTime()));

    // maxDate: earlier of the two latest dates
    const max = !ledgerMax ? checkMax : !checkMax ? ledgerMax : new Date(Math.min(ledgerMax.getTime(), checkMax.getTime()));

    return { minDate: min, maxDate: max };
  }, [ledgerEntries, checks]);

  // Filter data by date range
  const filteredLedger = useMemo(() => {
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
      return ledgerEntries;
    }

    return ledgerEntries.filter((entry) => {
      if (!entry.entry_date) return true;
      const entryDate = new Date(entry.entry_date);
      return entryDate >= dateRange[0] && entryDate <= dateRange[1];
    });
  }, [ledgerEntries, dateRange]);

  const filteredChecks = useMemo(() => {
    if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
      return checks;
    }

    return checks.filter((check) => {
      if (!check.check_date) return true;
      const checkDate = new Date(check.check_date);
      return checkDate >= dateRange[0] && checkDate <= dateRange[1];
    });
  }, [checks, dateRange]);

  const selectedLedgerEntry = filteredLedger.find((e) => e.id === selectedLedgerEntryId);
  const selectedChecks = filteredChecks.filter(
    (c) => c.id && selectedCheckIds.has(c.id)
  );

  const formatCurrency = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return '$0.00';
    const numeric = typeof value === 'number' ? value : parseFloat(String(value));
    if (Number.isNaN(numeric)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numeric);
  };

  const getTotalChecks = () => {
    return selectedChecks.reduce((sum, c) => {
      if (c.amount === null || c.amount === undefined) return sum;
      return sum + Math.abs(c.amount);
    }, 0);
  };

  const getLedgerAmount = () => {
    if (!selectedLedgerEntry) return 0;
    return Math.abs(selectedLedgerEntry.amount);
  };

  const handleLinkClick = () => {
    const totalChecks = getTotalChecks();
    const ledgerAmount = getLedgerAmount();

    if (totalChecks > ledgerAmount) {
      toast.current?.show({
        severity: 'error',
        summary: 'Linking blocked',
        detail: `Total checks (${formatCurrency(totalChecks)}) exceed ledger entry amount (${formatCurrency(ledgerAmount)})`,
      });
      return;
    }

    setShowConfirmDialog(true);
  };

  const handleConfirm = async () => {
    if (!selectedLedgerEntryId || selectedChecks.length === 0) return;

    setIsSaving(true);

    try {
      // Create allocation for each selected check (also links check to ledger entry)
      for (const check of selectedChecks) {
        await allocationsApi.createManualAllocation({
          client_id: clientId,
          ledger_entry_id: selectedLedgerEntryId,
          check_id: check.id,
          customer_id: check.customer_id || undefined,
          amount: check.amount || 0,
          notes: notes || undefined,
        });
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Manual match created',
        detail: `Successfully linked ${selectedChecks.length} check(s) to ledger entry`,
        life: 4000,
      });

      // Reset state
      setSelectedLedgerEntryId(null);
      setSelectedCheckIds(new Set());
      setCheckAmountFilter(null);
      setNotes('');
      setShowConfirmDialog(false);

      // Refresh data
      loadData();
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Manual match failed',
        detail: err?.response?.data?.detail || err?.message || 'Failed to create manual match',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const totalChecks = getTotalChecks();
  const ledgerAmount = getLedgerAmount();
  const remaining = ledgerAmount - totalChecks;

  const menuItems = [
    accountMenuItem,
    {
      label: 'Link',
      icon: 'ms-out ms-out-link',
      command: handleLinkClick,
      disabled: !selectedLedgerEntryId || selectedCheckIds.size === 0,
    },
    ...(selectedLedgerEntryId ? [{
      template: () => (
        <div className="menu-item-info">
          <span>Ledger: <strong>{formatCurrency(ledgerAmount)}</strong></span>
          {selectedCheckIds.size > 0 && (
            <>
              <span> - </span>
              <span>{selectedCheckIds.size} Check(s): <strong>{formatCurrency(totalChecks)}</strong></span>
              <span> = </span>
              <span>Remaining: <strong>{formatCurrency(remaining)}</strong></span>
            </>
          )}
        </div>
      ),
    }] : []),
  ];

  return (
    <>
      <Toast ref={toast} />

      <Menubar
        className="actions-menubar"
        model={menuItems}
        end={
          <div className="controls-group">
            <Calendar
              value={dateRange}
              onChange={(e) => setDateRange(e.value as Date[] | null)}
              selectionMode="range"
              minDate={minDate || undefined}
              maxDate={maxDate || undefined}
              placeholder="Filter by date range"
              showIcon
              numberOfMonths={3}
              dateFormat="yy-mm-dd"
              readOnlyInput
            />
            {dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1] && (
              <Button
                icon="ms-out ms-out-close"
                onClick={() => setDateRange(null)}
                size="small"
                severity="secondary"
                tooltip="Clear date filter"
              />
            )}
          </div>
        }
      />

      <Splitter>
        <SplitterPanel size={50} minSize={30}>
          <UnallocatedLedgerPanel
            entries={filteredLedger}
            loading={isLoading}
            selectionMode="single"
            selectedEntryId={selectedLedgerEntryId}
            onSelectionChange={setSelectedLedgerEntryId}
            onFilterByAmount={(amount) => setCheckAmountFilter(amount.toString())}
            title="Ledger Purchases"
            showCheckNo
          />
        </SplitterPanel>
        <SplitterPanel size={50} minSize={30}>
          <UnallocatedChecksPanel
            clientId={clientId}
            checks={filteredChecks}
            loading={isLoading}
            selectionMode="multiple"
            selectedCheckIds={selectedCheckIds}
            onSelectionChange={setSelectedCheckIds}
            selectedLedgerEntryDate={selectedLedgerEntry?.entry_date || null}
            externalAmountFilter={checkAmountFilter}
          />
        </SplitterPanel>
      </Splitter>

      <Dialog
        header="Confirm Manual Match"
        visible={showConfirmDialog}
        className="manual-match-dialog"
        onHide={() => setShowConfirmDialog(false)}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isSaving}
            />
            <Button
              label="Confirm Link"
              className="p-button-success"
              onClick={handleConfirm}
              loading={isSaving}
              disabled={isSaving}
            />
          </div>
        }
      >
        <>
          <table className="details-table">
            <tbody>
              <tr>
                <td>Total Checks</td>
                <td>{formatCurrency(totalChecks)}</td>
              </tr>
              <tr>
                <td>Ledger Entry Amount</td>
                <td>{formatCurrency(ledgerAmount)}</td>
              </tr>
              <tr>
                <td>Remaining Unallocated</td>
                <td>{formatCurrency(remaining)}</td>
              </tr>
            </tbody>
          </table>

          <div className="form-item">
            <label htmlFor="notes">
              <strong>Notes (optional):</strong>
            </label>
            <InputTextarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="full-width-input"
              placeholder="Add any notes about this manual match..."
            />
          </div>
        </>
      </Dialog>
    </>
  );
};
